import accounting from 'accounting';
import { symbolOfCurrency } from '../organisation/helper';
import { DropdownOption } from '@salvapp/ui-components/build/dropdown/dropdown.types';
import { getLocalisedDate } from '../common/datetime/FormattedDateTime';
import {
  InternalApiCurrentUserOrganisationCurrencyEnum,
  InternalApiEntitySchemaField,
  InternalApiEntitySchemaFieldType,
} from '../api-internal';
import { useDispatch } from 'react-redux';
import { showConfirmationModal } from '../confirmation-modal/confirmationModalReducer';

export const getSearchableFieldsByModification = (
  oldSchemaFields: InternalApiEntitySchemaField[],
  newSchemaFields: InternalApiEntitySchemaField[]
) => {
  const createdFields = newSchemaFields.filter((newField) => {
    const oldField = oldSchemaFields.find((oldField) => oldField.id === newField.id);
    if (!oldField) {
      return newField.searchable;
    } else {
      return !oldField.searchable && newField.searchable;
    }
  });

  const deletedFields = oldSchemaFields.filter((oldField) => {
    const newField = newSchemaFields.find((newField) => newField.id === oldField.id);
    if (!newField) {
      return oldField.searchable;
    } else {
      return oldField.searchable && !newField.searchable;
    }
  });

  const fieldsWithUpdatedImportantProperties = newSchemaFields.filter((newField) => {
    const oldField = oldSchemaFields.find((oldField) => oldField.id === newField.id);
    if (oldField && oldField.searchable && newField.searchable) {
      return hasImportantChanges(oldField, newField);
    } else {
      return false;
    }
  });

  return {
    createdFields,
    fieldsWithUpdatedImportantProperties,
    deletedFields,
  };
};

const hasImportantChanges = (oldField: InternalApiEntitySchemaField, newField: InternalApiEntitySchemaField) =>
  oldField.fieldName !== newField.fieldName || oldField.type !== newField.type;

export const useIndexAndSchemaAreOutOfSyncNotification = () => {
  const dispatch = useDispatch();

  return {
    notify: async () => {
      dispatch(
        showConfirmationModal({
          title: `The Entity Search is not synced with your Configuration!`,
          description:
            'If you are done with your changes notify Salv to synchronize Entity Search with your Configuration',
          onConfirm: null,
          cancelText: 'Close',
        })
      );
    },
  };
};

const getDisplayName = (schemaField: InternalApiEntitySchemaField | undefined) => {
  return schemaField?.displayName;
};

const isVisible = (schemaField: InternalApiEntitySchemaField | undefined) => {
  return !schemaField?.hidden;
};

const isChildField = (schemaField: InternalApiEntitySchemaField | undefined) => {
  return !!schemaField?.parentId;
};

const isComplexField = (schemaField: InternalApiEntitySchemaField | undefined) => {
  return schemaField?.type === InternalApiEntitySchemaFieldType.OBJECT || schemaField?.isMulti;
};

const getOrderedSchemaFieldsOfSearchResults = (schemaFields: InternalApiEntitySchemaField[]) => {
  return orderBySchemaFields(
    schemaFields,
    schemaFields.map((schemaField) => schemaField.fieldName)
  )
    .map((fieldName) => schemaFields.find((e) => e.fieldName === fieldName)!)
    .filter((schemaField) => !schemaField.hiddenInSearchResults);
};

const orderBySchemaFields = (
  schemaFields: InternalApiEntitySchemaField[] | undefined,
  attributeKeys: string[]
): string[] => {
  if (!schemaFields) {
    return attributeKeys;
  }
  return attributeKeys
    .filter((attribute) => {
      const field = schemaFields.find((e) => e.fieldName === attribute);
      return !field?.hidden;
    })
    .sort((a, b) => {
      const fieldA = schemaFields.find((e) => e.fieldName === a);
      const fieldB = schemaFields.find((e) => e.fieldName === b);

      if (fieldA && fieldA.sortOrder && fieldB && fieldB.sortOrder) {
        return fieldA.sortOrder - fieldB.sortOrder;
      } else if (fieldA && fieldA.sortOrder) {
        return -1;
      } else if (fieldB && fieldB.sortOrder) {
        return 1;
      }
      return a.localeCompare(b);
    });
};

const getDisplayValue = (
  schemaField: InternalApiEntitySchemaField | undefined,
  value: string | boolean | undefined
) => {
  if (typeof value === 'boolean') {
    return String(value);
  }

  if (!value) {
    return '';
  } else if (!schemaField) {
    return value;
  }

  switch (schemaField.type) {
    case InternalApiEntitySchemaFieldType.DATETIME:
      return getLocalisedDate(value);
    case InternalApiEntitySchemaFieldType.NUMBER: {
      return accounting.formatNumber(Number(value), 2, '\xa0');
    }
    case InternalApiEntitySchemaFieldType.OBJECT: {
      try {
        return JSON.stringify(JSON.parse(value), null, '\t');
      } catch (_) {
        return value;
      }
    }
    default:
      return value;
  }
};

const getSearchAttributeOptions = (schemaFields: InternalApiEntitySchemaField[]): DropdownOption[] => {
  return schemaFields
    .filter((field) => !isChildField(field) && field.searchable)
    .flatMap((field) => {
      if (field.type === InternalApiEntitySchemaFieldType.OBJECT) {
        return schemaFields
          .filter((child) => child.parentId === field.id)
          .map((childField) => ({
            key: childField.id,
            label: `${field.displayName} - ${childField.displayName}`,
            value: childField.id,
          }));
      }
      return [
        {
          key: field.id,
          label: field.displayName,
          value: field.id,
        },
      ];
    });
};

const getParentSchemaField = (
  childSchemaField: InternalApiEntitySchemaField,
  schemaFields: InternalApiEntitySchemaField[]
) => {
  if (!childSchemaField.parentId) {
    throw new Error(`Schema field ${childSchemaField.fieldName} has no parentId`);
  }

  return schemaFields.find((field) => field.id === childSchemaField.parentId)!;
};

const getAttributeClass = (schemaField: InternalApiEntitySchemaField | undefined): string | undefined => {
  if (schemaField && schemaField.type === InternalApiEntitySchemaFieldType.NUMBER) {
    return 'text-end';
  }

  return undefined;
};

export const getCurrencySymbol = (currency?: InternalApiCurrentUserOrganisationCurrencyEnum) => {
  if (currency) {
    return symbolOfCurrency[currency];
  }
};

export const nonBreakingSpace = '\xa0';

export const formatAmount = (amount: number, currency?: InternalApiCurrentUserOrganisationCurrencyEnum): string => {
  const symbol = getCurrencySymbol(currency) || '€';
  return accounting.formatMoney(amount, symbol, 2, '\xa0', '.', '%v\xa0%s');
};

export const formatIncomingAmount = (
  amount: number,
  currency?: InternalApiCurrentUserOrganisationCurrencyEnum
): string => {
  return `+${formatAmount(amount, currency)}`;
};

export const formatOutgoingAmount = (
  amount: number,
  currency?: InternalApiCurrentUserOrganisationCurrencyEnum
): string => {
  return `-${formatAmount(amount, currency)}`;
};

export const formatAmountsInText = (text: string): string => {
  return text.replace(/(\d+)(\.)?(\d{1,2})?/g, (match: string, p1: string, p2: string | null, p3: string | null) => {
    if (p1.charAt(0) === '0') return match;

    const precision = p3?.length ?? 0;
    return accounting.formatNumber(Number(match), precision, '\xa0');
  });
};

const isAttributeMatchingSchemaField = (schemaField: InternalApiEntitySchemaField, attribute: string) => {
  try {
    if (!schemaField || !attribute || !isComplexField(schemaField)) {
      return true;
    } else if (schemaField.isMulti && schemaField.type === InternalApiEntitySchemaFieldType.OBJECT) {
      const objectArray = JSON.parse(attribute);
      return (
        Array.isArray(objectArray) &&
        (!objectArray.length || (objectArray.length && objectArray.every((object) => typeof object === 'object')))
      );
    } else if (schemaField.type === InternalApiEntitySchemaFieldType.OBJECT) {
      const object = JSON.parse(attribute);
      return !Array.isArray(object) && typeof object === 'object';
    } else if (schemaField.isMulti) {
      const array = JSON.parse(attribute);
      return (
        Array.isArray(array) &&
        (!array.length || array.every((element) => typeof element !== 'object' || element === null))
      );
    }
  } catch (_) {
    return false;
  }
};

const formatArrayObjectAttribute = (
  parentSchemaField: InternalApiEntitySchemaField,
  schemaFields: InternalApiEntitySchemaField[] = [],
  attribute: string
) => {
  if (!isAttributeMatchingSchemaField(parentSchemaField, attribute)) {
    return attribute;
  }
  const objects = JSON.parse(attribute) as Array<{ [key: string]: string }>;
  const childSchemaFields = schemaFields?.filter((schemaField) => schemaField.parentId === parentSchemaField.id);

  let personAsText = '';
  for (const [index, object] of objects.entries()) {
    const orderedChildFieldNames = orderBySchemaFields(childSchemaFields, Object.keys(object));

    for (const childFieldName of orderedChildFieldNames) {
      const childField = childSchemaFields?.find((schemaField) => schemaField.fieldName === childFieldName);
      personAsText = personAsText.concat(
        `${childField?.displayName ?? childFieldName} ${index + 1}\t${object[childFieldName]}\n`
      );
    }
  }

  return personAsText;
};

const formatObjectAttribute = (
  parentSchemaField: InternalApiEntitySchemaField,
  schemaFields: InternalApiEntitySchemaField[] = [],
  attribute: string
) => {
  if (!isAttributeMatchingSchemaField(parentSchemaField, attribute)) {
    return attribute;
  }
  const object = JSON.parse(attribute) as { [key: string]: string };
  const childSchemaFields = schemaFields?.filter((schemaField) => schemaField.parentId === parentSchemaField.id);
  const orderedChildFieldNames = orderBySchemaFields(childSchemaFields, Object.keys(object));

  let personAsText = '';
  for (const childFieldName of orderedChildFieldNames) {
    const childField = childSchemaFields?.find((schemaField) => schemaField.fieldName === childFieldName);
    personAsText = personAsText.concat(`${childField?.displayName ?? childFieldName}\t${object[childFieldName]}\n`);
  }

  return personAsText;
};

const formatArrayAttribute = (schemaField: InternalApiEntitySchemaField, attribute: string) => {
  if (!isAttributeMatchingSchemaField(schemaField, attribute)) {
    return attribute;
  }
  const value = JSON.parse(attribute);
  return `${(Array.isArray(value) ? value : Array(value)).filter((value) => !!value).join(',')}`;
};

export {
  isVisible,
  isChildField,
  isComplexField,
  getDisplayName,
  getOrderedSchemaFieldsOfSearchResults,
  orderBySchemaFields,
  getDisplayValue,
  getSearchAttributeOptions,
  getParentSchemaField,
  getAttributeClass,
  isAttributeMatchingSchemaField,
  formatArrayObjectAttribute,
  formatObjectAttribute,
  formatArrayAttribute,
};
